import React from 'react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import Workflow from '../components/Workflow';
import ScreenshotGallery from '../components/ScreenshotGallery';
import TestimonialBlock from '../components/TestimonialBlock';
import IntroVideoModal from '../components/IntroVideoModal';
import { sectorVideos } from '../videos';
import {
  Building2, HardHat, Home, Warehouse, Wrench, Truck, Hammer,
  Layers, Ruler, Flame, Users, Briefcase,
} from 'lucide-react';

const CONSTRUCTION_SHOTS = [
  { src: '/images/construction/cp-live-portfolio.jpg', caption: 'Tier-1 live portfolio view, projects at every RIBA stage.' },
  { src: '/images/construction/cp-portfolio-pmo.jpg', caption: 'Construction PMO, programme, commercial and risk in one place.' },
  { src: '/images/construction/cp-bid-pipeline.jpg', caption: 'Bid pipeline with AI-assisted qualification and scoring.' },
  { src: '/images/construction/cp-project-stages.jpg', caption: 'Stage-gated delivery, governance evidence auto-compiled per gate.' },
  { src: '/images/construction/cp-safety-rams.jpg', caption: 'Safety, RAMS and CDM 2015 evidence built into the pathway.' },
  { src: '/images/construction/cp-lifecycle.jpg', caption: 'Handover creates the operational spine, not the end of information.' },
  { src: '/images/construction/cp-executive-report.jpg', caption: 'Executive narrative report, plain English, live data.' },
];

const V2VideraConstruction = () => {
  useSEO({
    title: 'Videra Construction | Governed delivery from bid to handover',
    description: 'Videra Construction brings governance, programme, risk, evidence and handover into one structured project delivery environment for Tier-1 delivery.',
    path: '/videra/construction',
  });

  return (
    <div data-testid="v2-videra-construction">
      <ProductHero
        eyebrow="Videra Construction"
        title="Better visibility from opportunity to handover."
        body={(
          <>
            <p>Videra Construction brings project governance, programme visibility, risk, evidence, approvals and handover into a structured delivery environment.</p>
            <p>Construction creates assets that may operate for decades. Good project closeout should therefore create the beginning of the operational and lifecycle record, not the end of the information.</p>
          </>
        )}
        primary={{ label: 'Explore Videra Construction', href: 'https://demo.videra.app/explore' }}
        secondary={{ label: 'Talk to Keystone', to: '/contact' }}
        heroImage="/images/construction/cp-live-portfolio.jpg"
        heroImageAlt="Videra Construction Tier-1 live portfolio"
        testid="construction-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Construction journey</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-10">From opportunity through operation.</h2>
          <Workflow testid="construction-workflow" steps={['Opportunity','Design','Governance','Procurement','Construction','Commission','Evidence','Handover','Operation / Lifecycle']} />
        </div>
      </section>

      {/* Suitable for every construction business */}
      <section className="py-20 md:py-24 bg-white" data-testid="construction-business-types">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold mb-3">Built for every construction business</p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
                One platform to run the business, deliver the projects and manage every client.
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Videra Construction is not just for Tier-1 contractors. It is configured for every construction-related business, track pipeline, projects, safety, commercial, evidence, handover and the full client relationship in one environment.
              </p>
            </div>
            <div className="text-sm text-slate-500 max-w-xs">
              <p className="font-semibold text-slate-900 mb-1">All in one place:</p>
              <p>Pipeline · Bids · Projects · Programme · Commercial · Safety · Subcontractors · Evidence · Handover · Client CRM</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { icon: Building2, name: 'Main / Tier-1 contractors', body: 'Programme, commercial and governance across live schemes.' },
              { icon: HardHat, name: 'Civil engineering & infrastructure', body: 'Multi-package delivery with commercial and evidence rails.' },
              { icon: Home, name: 'Housebuilders & residential developers', body: 'Plot delivery, sales pipeline and warranty evidence.' },
              { icon: Briefcase, name: 'Commercial developers', body: 'Scheme pipeline, tenant fit-out and handover packs.' },
              { icon: Wrench, name: 'M&E and mechanical contractors', body: 'Package delivery, commissioning and O&M evidence.' },
              { icon: Truck, name: 'Groundworks & demolition', body: 'Site logistics, RAMS, waste and evidence capture.' },
              { icon: Layers, name: 'Modular & offsite manufacturers', body: 'Factory-to-site programme, quality and traceability.' },
              { icon: Hammer, name: 'Refurbishment & fit-out specialists', body: 'Multi-site programme, snag close-out and handover.' },
              { icon: Ruler, name: 'Specialist subcontractors', body: 'Roofing, cladding, glazing, facades, RAMS, evidence, invoicing.' },
              { icon: Flame, name: 'Firestopping & passive fire', body: 'Survey, remediation and audit trail, powered by SafeTrace.' },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.name}
                  data-testid={`construction-business-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-orange-500 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center mb-3 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <p className="text-sm font-semibold text-slate-900 leading-tight mb-1.5">{b.name}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>

          {/* Client management callout */}
          <div className="mt-10 bg-slate-900 text-white rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="construction-client-mgmt">
            <div className="w-12 h-12 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.22em] text-orange-400 font-semibold mb-2">Full client management, not bolted on</p>
              <p className="text-lg md:text-xl leading-snug">
                Contacts, opportunities, contracts, live projects, variations, invoices and post-handover care, every client's full history in one thread.
              </p>
            </div>
            <a
              href="https://demo.videra.app/explore"
              data-testid="construction-business-cta"
              className="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-3 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors flex-shrink-0"
            >
              Try it free, 14 days fully unlocked
            </a>
          </div>
        </div>
      </section>

      <ScreenshotGallery
        eyebrow="Inside Videra Construction"
        title="Structured delivery from bid to handover."
        description="Sample screens from the vertically-integrated Tier-1 contractor environment."
        shots={CONSTRUCTION_SHOTS}
        testid="construction-gallery"
      />

      <section className="py-16 md:py-20" data-testid="construction-video">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Videra Construction+ walk-through</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
              Watch the Construction+ environment in action.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              A short walk-through of the vertically-integrated Tier-1 delivery environment, bid, design, procurement, safety, delivery, handover.
            </p>
          </div>
          <IntroVideoModal
            src={sectorVideos.construction?.videoUrl}
            poster="/images/construction/cp-welcome-splash.jpg"
            kicker="Construction+"
            title="Watch the walk-through"
            testid="construction-video-modal"
          />
        </div>
      </section>

      <TestimonialBlock product="videra-construction" />
    </div>
  );
};

export default V2VideraConstruction;
